import {Purchases} from '~/modules/api/api.types';
import {useWebPurchasesLookupQuery} from '~/store/rtkQuery';
import {formatDate} from '~/modules/utils';

interface WebPurchasesProps {
  query: string;
  // Renders nothing while loading or when there are no purchases. Errors still show.
  hideWhenEmpty?: boolean;
}

/**
 * Stripe web purchases bought with the searched email, or redeemed by the
 * searched user, with each code's redemption status
 */
export const WebPurchases = ({query, hideWhenEmpty}: WebPurchasesProps) => {
  const {data, error, isFetching} = useWebPurchasesLookupQuery(
    {query},
    {refetchOnMountOrArgChange: true},
  );

  const isEmpty = isFetching || !data || data.purchases.length === 0;
  if (hideWhenEmpty && !error && isEmpty) {
    return null;
  }

  return (
    <div className="card outline outline-zinc-200 outline-1">
      <div className="card-body">
        <h2 className="card-title">Web purchases</h2>
        {isFetching ? (
          <p className="text-sm text-gray-600">Loading web purchases...</p>
        ) : error ? (
          <div role="alert" className="alert alert-error">
            <span>{error.message}</span>
          </div>
        ) : !data || data.purchases.length === 0 ? (
          <p className="text-sm text-gray-600">
            No web purchases for this {query.includes('@') ? 'email' : 'user'}.
          </p>
        ) : (
          <div className="space-y-3">
            {data.purchases.map(purchase => (
              <WebPurchaseRow key={purchase.purchaseId} purchase={purchase} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WebPurchaseRow = ({purchase}: {purchase: Purchases.WebPurchase}) => (
  <div className="border-b border-base-200 pb-2 last:border-0 last:pb-0">
    <div className="flex flex-wrap items-center gap-2">
      <p className="font-medium">
        {purchase.productName ?? purchase.productId}
      </p>
      <span className="badge badge-sm badge-outline">
        {purchase.type === 'subscription' ? 'Subscription' : 'One-off'}
      </span>
      {purchase.subscription && (
        <span
          className={`badge badge-sm ${
            purchase.subscription.status === 'active'
              ? 'badge-success'
              : 'badge-error font-bold'
          }`}>
          {purchase.subscription.status === 'active' ? 'Active' : 'INACTIVE'}
        </span>
      )}
      {purchase.matchedBy.includes('redeemed') && (
        <span className="badge badge-sm badge-info">Redeemed by this user</span>
      )}
      {purchase.linkedUserId && (
        <span className="badge badge-sm badge-outline">
          Linked to account {purchase.linkedUserId}
        </span>
      )}
      {!purchase.isLiveMode && (
        <span className="badge badge-sm badge-warning">Test</span>
      )}
    </div>
    <p className="text-sm text-gray-600">
      <span className="font-bold">Purchased:</span> {formatDate(purchase.date)}
    </p>
    <p className="text-sm text-gray-600 break-words">
      <span className="font-bold">Buyer email:</span> {purchase.buyerEmail}
    </p>
    {purchase.promotionCode && (
      <p className="text-sm text-gray-600">
        <span className="font-bold">Promo code:</span> {purchase.promotionCode}
      </p>
    )}
    {purchase.subscription &&
      (purchase.subscription.status === 'active' ? (
        <>
          <p className="text-sm text-gray-600">
            <span className="font-bold">Next renewal:</span>{' '}
            {formatDate(purchase.subscription.nextRenewalAt)}
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-bold">Auto-renew:</span>{' '}
            {purchase.subscription.autoRenewEnabled === null
              ? '-'
              : purchase.subscription.autoRenewEnabled
                ? 'Yes'
                : 'No'}
          </p>
        </>
      ) : (
        <p className="text-sm text-gray-600">
          <span className="font-bold">Ended:</span>{' '}
          {formatDate(purchase.subscription.activeUntil)}
        </p>
      ))}
    {/* Purchases linked to an account attach entitlements directly and
        never issue codes. */}
    {!purchase.linkedUserId && (
      <>
        <p className="mt-1 text-sm font-bold text-gray-600">Codes</p>
        {purchase.codes.length === 0 ? (
          <p className="text-sm text-gray-600">No codes issued.</p>
        ) : (
          <ul className="space-y-1">
            {purchase.codes.map(code => (
              <li
                key={code.code}
                className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="font-mono">{code.code}</span>
                <span
                  className={`badge badge-sm ${
                    code.status === 'redeemed' ? 'badge-success' : 'badge-ghost'
                  }`}>
                  {code.status === 'redeemed' ? 'Redeemed' : 'Unredeemed'}
                </span>
                {code.redeemedByUserId && (
                  <span className="break-words">
                    {formatDate(code.redeemedDate)} by{' '}
                    <span className="font-mono">{code.redeemedByUserId}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </>
    )}
  </div>
);
